/**
 * Location Search Async Typeahead component for NSW
 * This component should be used both the in the SearchFormWithCommitment and the OrganisationSearchForm.
 */

import {
  AsyncTypeaheadProps,
  AsyncTypeaheadSearch,
  FilterName,
  FriendlyKeyValueUrl,
} from "@skv/core";
import { useCallback } from "react";
import { ActorRefFrom } from "xstate";
import { searchFormMachine } from "./SearchForm.state";

interface LocationSearchTypeaheadProps {
  formRef: React.MutableRefObject<HTMLFormElement | null>;
  actorRef: ActorRefFrom<typeof searchFormMachine>;
  openFilter: FilterName | null;
  setOpenFilter: React.Dispatch<React.SetStateAction<FilterName | null>>;
  isRepeatSearch: boolean;
  onOptionSelected: (
    selected: Array<FriendlyKeyValueUrl | string>,
    filterName: FilterName
  ) => void;
}

type RenderMenuFunction = NonNullable<
  AsyncTypeaheadProps<string>["renderMenu"]
>;

export const LocationSearchTypeahead = ({
  actorRef,
  openFilter,
  setOpenFilter,
  isRepeatSearch,
  onOptionSelected,
  formRef,
}: LocationSearchTypeaheadProps) => {
  const snapshot = actorRef.getSnapshot();

  const renderMenu = useCallback<RenderMenuFunction>(
    (results, menuProps, { Menu, MenuItem, MenuHeader }) => {
      const snapshot = actorRef.getSnapshot();

      if (
        snapshot?.matches("initialSuggestions.loading") ||
        snapshot?.matches("relatedSuggestions.loading")
      ) {
        return (
          <Menu
            {...menuProps}
            ref={menuProps.ref as React.MutableRefObject<HTMLUListElement>}
            aria-labelledby="where-heading"
          >
            <MenuHeader id="where-heading">
              <p className="mt-3">Loading please wait...</p>
            </MenuHeader>
          </Menu>
        );
      }

      // render initial suggestions if where input is < 2 chars
      if (snapshot && snapshot.context.where.length <= 2) {
        return (
          <Menu
            {...menuProps}
            ref={menuProps.ref as React.MutableRefObject<HTMLUListElement>}
            aria-labelledby="where-heading"
          >
            <MenuHeader
              id="remote-heading"
              className="dropdown-header font-weight-bold"
            >
              Remote
            </MenuHeader>
            <MenuItem option={results[0]} position={0} />
            <MenuHeader
              id="where-heading"
              className="dropdown-header font-weight-bold"
            >
              Suggested areas
            </MenuHeader>
            {results.map((result, idx) => {
              if (idx === 0) {
                return null;
              }
              return <MenuItem key={idx} option={result} position={idx} />;
            })}
          </Menu>
        );
      }

      return (
        <Menu
          {...menuProps}
          ref={menuProps.ref as React.MutableRefObject<HTMLUListElement>}
          aria-labelledby="where-heading"
        >
          {results.length ? (
            results.map((result, idx) => (
              <MenuItem key={idx} option={result} position={idx} />
            ))
          ) : (
            <MenuHeader id="where-heading">No results</MenuHeader>
          )}
        </Menu>
      );
    },
    [actorRef]
  );

  if (!snapshot) return null;

  return (
    <>
      <label className="nsw-form__label" htmlFor="where">
        Location
      </label>
      <span className="sr-only">Enter suburb, town or city</span>
      <AsyncTypeaheadSearch<string>
        id="where"
        inputProps={{
          id: "where",
          name: "where",
          autoComplete: "off",
          className: "nsw-form__input focus-primary border-primary",
          placeholder: "Enter suburb, town or city",
        }}
        open={openFilter === "where"}
        onClick={() => {
          if (openFilter !== "where") {
            setOpenFilter("where");
          }
        }}
        onFocus={() => setOpenFilter("where")}
        onBlur={() => setOpenFilter(null)}
        onInputChange={(text) => {
          actorRef.send({ type: "LOCATION_INPUT_CHANGE", value: text });
          if (openFilter !== "where") {
            setOpenFilter("where");
          }
        }}
        onSearch={(text) => {
          actorRef.send({ type: "FETCH_START_LOCATIONS", value: text });
        }}
        onChange={(selected) => onOptionSelected(selected, "where")}
        onClear={() => {
          Promise.resolve().then(() => {
            actorRef.send({ type: "LOCATION_INPUT_CLEAR" });
            setOpenFilter(null);

            if (isRepeatSearch) {
              formRef.current?.requestSubmit();
            }
          });
        }}
        isLoading={snapshot.matches("relatedSuggestions.loading")}
        options={(() => {
          if (snapshot.context.where.length <= 2) {
            return snapshot.context.initialSuggestions;
          }

          return snapshot.context.relatedSuggestions || [];
        })()}
        defaultInputValue={snapshot.context.where}
        selected={snapshot.context.where ? [snapshot.context.where] : []}
        minLength={0}
        onKeyDown={(e) => {
          switch (e.key) {
            case "Escape":
              if (openFilter === "where") {
                setOpenFilter(null);
              }
              break;
            case "Enter":
              formRef.current?.requestSubmit();
              break;
            default:
              break;
          }
        }}
        renderMenu={renderMenu}
      />
    </>
  );
};
